/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Verdi Dashboard API
 * API behind the Verdi irrigation dashboard: organizations, their devices, zones and irrigation schedules, and the sensor data those devices report.
 *
 * ## Authentication
 *
 * Most operations need a Descope session token, sent as `Authorization: Bearer <token>`. Each operation's `security` lists what it accepts. Role and permission names there, such as `Verdi Administrator`, are Descope roles or permissions the session must carry, and the operation's description repeats them under **Access**. Operations with `security: []` are public.
 *
 * ## Organization context
 *
 * Data belongs to organizations. A user's session acts as their primary organization, or as another organization they belong to when its ID is sent in the `User` header. Administrators must send the `User` header on organization-scoped operations, or the request is rejected with a 400.
 *
 * ## Errors
 *
 * Error bodies are JSON `{ "message": string }`.
 *
 * - **400**: the path, query or body failed validation, or the request broke a domain rule.
 * - **401 / 403**: sent by the auth middleware; the body may be empty.
 * - **409**: see below.
 * - **500**: an unexpected server error.
 *
 * ## Versioned resources
 *
 * Versioned resources carry a `versionId`. Send the `versionId` you last read on a PATCH (in the body) or a DELETE (as a query parameter). If the resource changed since, the request gets a 409: reload it and retry. A 409 that includes a `code` is a conflict specific to that operation, described on the operation. On a create, 409 means the resource already exists.
 *
 * ## Reads with a request body
 *
 * Some reads take their input as a JSON body on the HTTP `QUERY` method; these operations are marked `x-read-only: true`. Deprecated POST and GET variants serve clients that cannot send `QUERY`.
 *
 * ## Identifiers
 *
 * MongoDB ObjectIds are 24-character hexadecimal strings.
 * OpenAPI spec version: 1.0.0
 */

import type {
	DataTag,
	DefinedInitialDataOptions,
	DefinedUseQueryResult,
	MutationFunction,
	QueryClient,
	QueryFunction,
	QueryKey,
	UndefinedInitialDataOptions,
	UseMutationOptions,
	UseMutationResult,
	UseQueryOptions,
	UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "../custom-fetch.ts";
import type {
	CreateZone400,
	CreateZone403,
	CreateZone422,
	CreateZone500,
	CreateZone503,
	DeleteZone400,
	DeleteZone404,
	DeleteZone500,
	GetZone400,
	GetZone404,
	GetZone500,
	ListZones400,
	ListZones500,
	UpdateZone400,
	UpdateZone403,
	UpdateZone404,
	UpdateZone422,
	UpdateZone500,
	UpdateZone503,
	Zone,
	ZoneCreate,
	ZoneUpdate,
} from "./schemas";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
	query: T,
	queryKey: K,
): T & { queryKey: K } => {
	const result = { queryKey } as T & { queryKey: K };
	for (const key of Object.keys(query)) {
		// The explicit queryKey always wins, matching the previous
		// `{ ...query, queryKey }` spread where it was set last.
		if (key === "queryKey") continue;
		Object.defineProperty(result, key, {
			enumerable: true,
			configurable: true,
			get: () => (query as Record<string, unknown>)[key],
		});
	}
	return result;
};

export const getListZonesUrl = () => {
	return `/zones`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `Organization` or `User` header.
 * @summary List all zones for the authenticated user
 */
export const listZones = async (
	options?: Parameters<typeof customFetch>[1],
): Promise<Zone[]> => {
	return customFetch<Zone[]>(getListZonesUrl(), {
		...options,
		method: "GET",
	});
};

export const getListZonesQueryKey = () => {
	return ["zones"] as const;
};

export const getListZonesQueryOptions = <
	TData = Awaited<ReturnType<typeof listZones>>,
	TError = ListZones400 | void | ListZones500,
>(options?: {
	query?: Partial<
		UseQueryOptions<Awaited<ReturnType<typeof listZones>>, TError, TData>
	>;
	request?: SecondParameter<typeof customFetch>;
}) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getListZonesQueryKey();

	const queryFn: QueryFunction<Awaited<ReturnType<typeof listZones>>> = ({
		signal,
	}) => listZones({ signal, ...requestOptions });

	return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
		Awaited<ReturnType<typeof listZones>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListZonesQueryResult = NonNullable<
	Awaited<ReturnType<typeof listZones>>
>;
export type ListZonesQueryError = ListZones400 | void | ListZones500;

export function useListZones<
	TData = Awaited<ReturnType<typeof listZones>>,
	TError = ListZones400 | void | ListZones500,
>(
	options: {
		query: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof listZones>>, TError, TData>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof listZones>>,
					TError,
					Awaited<ReturnType<typeof listZones>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListZones<
	TData = Awaited<ReturnType<typeof listZones>>,
	TError = ListZones400 | void | ListZones500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof listZones>>, TError, TData>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof listZones>>,
					TError,
					Awaited<ReturnType<typeof listZones>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListZones<
	TData = Awaited<ReturnType<typeof listZones>>,
	TError = ListZones400 | void | ListZones500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof listZones>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List all zones for the authenticated user
 */

export function useListZones<
	TData = Awaited<ReturnType<typeof listZones>>,
	TError = ListZones400 | void | ListZones500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof listZones>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getListZonesQueryOptions(options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List all zones for the authenticated user
 */
export const useSetListZonesQueryData = () => {
	const queryClient = useQueryClient();
	return (
		updater:
			| Awaited<ReturnType<typeof listZones>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof listZones>> | undefined,
			  ) => Awaited<ReturnType<typeof listZones>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<Awaited<ReturnType<typeof listZones>>>(
			{ exact: $exactMatch, queryKey: getListZonesQueryKey() },
			updater,
		);
	};
};

/**
 * @summary List all zones for the authenticated user
 */
export const useGetListZonesQueryData = () => {
	const queryClient = useQueryClient();
	return () =>
		queryClient.getQueryData<Awaited<ReturnType<typeof listZones>>>(
			getListZonesQueryKey(),
		);
};

export const getCreateZoneUrl = () => {
	return `/zones`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `Organization` or `User` header.
 * @summary Create a new zone
 */
export const createZone = async (
	zoneCreate: ZoneCreate,
	options?: Parameters<typeof customFetch>[1],
): Promise<Zone> => {
	const getHeaders = (
		h?: NonNullable<RequestInit["headers"]>,
	): Record<string, string | readonly string[]> => {
		if (!h) return {};
		if (h instanceof Headers) return Object.fromEntries(h.entries());
		if (Symbol.iterator in h) {
			return Object.fromEntries(
				Array.from(
					h as Iterable<Iterable<string>>,
					(entry) => Array.from(entry) as [string, string],
				),
			);
		}
		const headers: Record<string, string | readonly string[]> = {};
		for (const [name, value] of Object.entries<
			string | readonly string[] | undefined
		>(h)) {
			if (value !== undefined) headers[name] = value;
		}
		return headers;
	};
	return customFetch<Zone>(getCreateZoneUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...getHeaders(options?.headers),
		},
		body: JSON.stringify(zoneCreate),
	});
};

export const getCreateZoneMutationKey = () => ["createZone"] as const;

export const getCreateZoneMutationOptions = <
	TError =
		| CreateZone400
		| void
		| CreateZone403
		| CreateZone422
		| CreateZone500
		| CreateZone503,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof createZone>>,
		TError,
		CreateZoneMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof createZone>>,
	TError,
	CreateZoneMutationVariables,
	TContext
> => {
	const mutationKey = getCreateZoneMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof createZone>>,
		CreateZoneMutationVariables
	> = (props) => {
		const { data } = props ?? {};

		return createZone(data, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type CreateZoneMutationResult = NonNullable<
	Awaited<ReturnType<typeof createZone>>
>;
export type CreateZoneMutationBody = ZoneCreate;
export type CreateZoneMutationError =
	| CreateZone400
	| void
	| CreateZone403
	| CreateZone422
	| CreateZone500
	| CreateZone503;
export type CreateZoneMutationVariables = { data: ZoneCreate };

/**
 * @summary Create a new zone
 */
export const useCreateZone = <
	TError =
		| CreateZone400
		| void
		| CreateZone403
		| CreateZone422
		| CreateZone500
		| CreateZone503,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof createZone>>,
			TError,
			CreateZoneMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof createZone>>,
	TError,
	CreateZoneMutationVariables,
	TContext
> => {
	return useMutation(getCreateZoneMutationOptions(options), queryClient);
};
export const getGetZoneUrl = (zoneId: string) => {
	return `/zones/${zoneId}`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `Organization` or `User` header.
 * @summary Get a single zone by ID
 */
export const getZone = async (
	zoneId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<Zone> => {
	return customFetch<Zone>(getGetZoneUrl(zoneId), {
		...options,
		method: "GET",
	});
};

export const getGetZoneQueryKey = (zoneId: string) => {
	return ["zones", zoneId] as const;
};

export const getGetZoneQueryOptions = <
	TData = Awaited<ReturnType<typeof getZone>>,
	TError = GetZone400 | void | GetZone404 | GetZone500,
>(
	zoneId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getZone>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getGetZoneQueryKey(zoneId);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof getZone>>> = ({
		signal,
	}) => getZone(zoneId, { signal, ...requestOptions });

	return {
		queryKey,
		queryFn,
		enabled: zoneId !== null && zoneId !== undefined,
		...queryOptions,
	} as UseQueryOptions<Awaited<ReturnType<typeof getZone>>, TError, TData> & {
		queryKey: DataTag<QueryKey, TData, TError>;
	};
};

export type GetZoneQueryResult = NonNullable<
	Awaited<ReturnType<typeof getZone>>
>;
export type GetZoneQueryError = GetZone400 | void | GetZone404 | GetZone500;

export function useGetZone<
	TData = Awaited<ReturnType<typeof getZone>>,
	TError = GetZone400 | void | GetZone404 | GetZone500,
>(
	zoneId: string,
	options: {
		query: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getZone>>, TError, TData>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof getZone>>,
					TError,
					Awaited<ReturnType<typeof getZone>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetZone<
	TData = Awaited<ReturnType<typeof getZone>>,
	TError = GetZone400 | void | GetZone404 | GetZone500,
>(
	zoneId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getZone>>, TError, TData>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof getZone>>,
					TError,
					Awaited<ReturnType<typeof getZone>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetZone<
	TData = Awaited<ReturnType<typeof getZone>>,
	TError = GetZone400 | void | GetZone404 | GetZone500,
>(
	zoneId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getZone>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get a single zone by ID
 */

export function useGetZone<
	TData = Awaited<ReturnType<typeof getZone>>,
	TError = GetZone400 | void | GetZone404 | GetZone500,
>(
	zoneId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getZone>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getGetZoneQueryOptions(zoneId, options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get a single zone by ID
 */
export const useSetGetZoneQueryData = () => {
	const queryClient = useQueryClient();
	return (
		zoneId: string,
		updater:
			| Awaited<ReturnType<typeof getZone>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof getZone>> | undefined,
			  ) => Awaited<ReturnType<typeof getZone>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<Awaited<ReturnType<typeof getZone>>>(
			{ exact: $exactMatch, queryKey: getGetZoneQueryKey(zoneId) },
			updater,
		);
	};
};

/**
 * @summary Get a single zone by ID
 */
export const useGetGetZoneQueryData = () => {
	const queryClient = useQueryClient();
	return (zoneId: string) =>
		queryClient.getQueryData<Awaited<ReturnType<typeof getZone>>>(
			getGetZoneQueryKey(zoneId),
		);
};

export const getUpdateZoneUrl = (zoneId: string) => {
	return `/zones/${zoneId}`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `Organization` or `User` header.
 * @summary Update an existing zone
 */
export const updateZone = async (
	zoneId: string,
	zoneUpdate: ZoneUpdate,
	options?: Parameters<typeof customFetch>[1],
): Promise<Zone> => {
	const getHeaders = (
		h?: NonNullable<RequestInit["headers"]>,
	): Record<string, string | readonly string[]> => {
		if (!h) return {};
		if (h instanceof Headers) return Object.fromEntries(h.entries());
		if (Symbol.iterator in h) {
			return Object.fromEntries(
				Array.from(
					h as Iterable<Iterable<string>>,
					(entry) => Array.from(entry) as [string, string],
				),
			);
		}
		const headers: Record<string, string | readonly string[]> = {};
		for (const [name, value] of Object.entries<
			string | readonly string[] | undefined
		>(h)) {
			if (value !== undefined) headers[name] = value;
		}
		return headers;
	};
	return customFetch<Zone>(getUpdateZoneUrl(zoneId), {
		...options,
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
			...getHeaders(options?.headers),
		},
		body: JSON.stringify(zoneUpdate),
	});
};

export const getUpdateZoneMutationKey = () => ["updateZone"] as const;

export const getUpdateZoneMutationOptions = <
	TError =
		| UpdateZone400
		| void
		| UpdateZone403
		| UpdateZone404
		| UpdateZone422
		| UpdateZone500
		| UpdateZone503,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof updateZone>>,
		TError,
		UpdateZoneMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof updateZone>>,
	TError,
	UpdateZoneMutationVariables,
	TContext
> => {
	const mutationKey = getUpdateZoneMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof updateZone>>,
		UpdateZoneMutationVariables
	> = (props) => {
		const { zoneId, data } = props ?? {};

		return updateZone(zoneId, data, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type UpdateZoneMutationResult = NonNullable<
	Awaited<ReturnType<typeof updateZone>>
>;
export type UpdateZoneMutationBody = ZoneUpdate;
export type UpdateZoneMutationError =
	| UpdateZone400
	| void
	| UpdateZone403
	| UpdateZone404
	| UpdateZone422
	| UpdateZone500
	| UpdateZone503;
export type UpdateZoneMutationVariables = { zoneId: string; data: ZoneUpdate };

/**
 * @summary Update an existing zone
 */
export const useUpdateZone = <
	TError =
		| UpdateZone400
		| void
		| UpdateZone403
		| UpdateZone404
		| UpdateZone422
		| UpdateZone500
		| UpdateZone503,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof updateZone>>,
			TError,
			UpdateZoneMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof updateZone>>,
	TError,
	UpdateZoneMutationVariables,
	TContext
> => {
	return useMutation(getUpdateZoneMutationOptions(options), queryClient);
};
export const getDeleteZoneUrl = (zoneId: string) => {
	return `/zones/${zoneId}`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `Organization` or `User` header.
 * @summary Delete a zone
 */
export const deleteZone = async (
	zoneId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<Zone> => {
	return customFetch<Zone>(getDeleteZoneUrl(zoneId), {
		...options,
		method: "DELETE",
	});
};

export const getDeleteZoneMutationKey = () => ["deleteZone"] as const;

export const getDeleteZoneMutationOptions = <
	TError = DeleteZone400 | void | DeleteZone404 | DeleteZone500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof deleteZone>>,
		TError,
		DeleteZoneMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof deleteZone>>,
	TError,
	DeleteZoneMutationVariables,
	TContext
> => {
	const mutationKey = getDeleteZoneMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof deleteZone>>,
		DeleteZoneMutationVariables
	> = (props) => {
		const { zoneId } = props ?? {};

		return deleteZone(zoneId, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type DeleteZoneMutationResult = NonNullable<
	Awaited<ReturnType<typeof deleteZone>>
>;

export type DeleteZoneMutationError =
	| DeleteZone400
	| void
	| DeleteZone404
	| DeleteZone500;
export type DeleteZoneMutationVariables = { zoneId: string };

/**
 * @summary Delete a zone
 */
export const useDeleteZone = <
	TError = DeleteZone400 | void | DeleteZone404 | DeleteZone500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof deleteZone>>,
			TError,
			DeleteZoneMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof deleteZone>>,
	TError,
	DeleteZoneMutationVariables,
	TContext
> => {
	return useMutation(getDeleteZoneMutationOptions(options), queryClient);
};
